import React from 'react';
import { Meeting } from '@/types/meeting';
import { MeetingCard } from './MeetingCard';
import { motion } from 'framer-motion';

type TabType = 'ALL' | Meeting['status'];

interface Tab {
  id: TabType;
  label: string;
}

const TABS: Tab[] = [
  { id: 'ALL', label: 'View All' },
  { id: 'SCHEDULED', label: 'Scheduled' },
  { id: 'IN_PROGRESS', label: 'In-Progress' },
  { id: 'COMPLETED', label: 'Completed' },
  { id: 'POSTPONED', label: 'Postponed/Rescheduled' },
  { id: 'CANCELLED', label: 'Cancelled' },
];

interface MeetingTabsProps {
  meetings: Meeting[];
  onEditMeeting: (meeting: Meeting) => void;
}

export function MeetingTabs({ meetings, onEditMeeting }: MeetingTabsProps) {
  const [activeTab, setActiveTab] = React.useState<TabType>('ALL');

  const filteredMeetings = React.useMemo(() => {
    if (activeTab === 'ALL') return meetings;
    return meetings.filter(meeting => meeting.status === activeTab);
  }, [meetings, activeTab]);

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto scrollbar-hide" aria-label="Meeting tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                group relative whitespace-nowrap py-4 px-1 border-b-2 font-medium text-xl
                transition-colors duration-200 ease-in-out
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-center">
                <span>{tab.label}</span>
                <span className={`
                  ml-2 text-xl font-semibold inline-flex items-center justify-center 
                  min-w-[2rem] h-7 rounded-full transition-all duration-200
                  ${tab.id === 'ALL' 
                    ? 'bg-blue-100 text-blue-800'
                    : tab.id === 'SCHEDULED'
                    ? 'bg-green-100 text-green-800'
                    : tab.id === 'IN_PROGRESS'
                    ? 'bg-yellow-100 text-yellow-800'
                    : tab.id === 'COMPLETED'
                    ? 'bg-gray-100 text-gray-800'
                    : tab.id === 'POSTPONED'
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-red-100 text-red-800'
                  }
                  ${activeTab === tab.id ? 'scale-110' : 'group-hover:scale-105'}
                `}>
                  {tab.id === 'ALL' 
                    ? meetings.length
                    : meetings.filter(m => m.status === tab.id).length
                  }
                </span>
              </div>
              {activeTab === tab.id && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-blue-500"
                  initial={false}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
        {filteredMeetings.map((meeting) => (
          <MeetingCard
            key={meeting.id}
            meeting={meeting}
            onClick={() => console.log('Meeting clicked:', meeting.id)}
            onEdit={() => onEditMeeting(meeting)}
          />
        ))}
        {filteredMeetings.length === 0 && (
          <div className="col-span-2 text-center py-12">
            <p className="text-gray-500">No meetings found in this category</p>
          </div>
        )}
      </div>
    </div>
  );
}